import React, { createContext } from "react";
import { I18nProvider as LinguiProvider } from "@lingui/react";
import { defaultLanguage } from "src/utils/localization/languages/languages";
import { Language } from "src/utils/localization/languages/types";
import { i18n, setupI18n, I18n, Messages } from "@lingui/core";
import { LanguageCode } from "src/utils/localization/languages/types";

// One I18n instance per locale. Gatsby renders pages of different locales
// concurrently in the same worker and late Suspense boundaries resolve after
// the next page has started, so anything reading a shared "active" locale
// picks up whatever was activated last (EN pages shipped DE strings).
const instances = new Map<string, I18n>();

export function i18nForLocale(code: string): I18n {
  let instance = instances.get(code);
  if (!instance) {
    instance = setupI18n({ locale: code, messages: { [code]: {} } });
    instances.set(code, instance);
  }
  return instance;
}

// Locales whose catalog has been registered. During SSR (and in Jest)
// ssrCatalogs.ts fills this synchronously with every locale; in the browser
// each page loads only its own catalog via activateLocale below — the other
// four never reach the client bundle.
export const loadedLocales = new Set<string>();

export function registerCatalog(code: string, messages: Messages): void {
  i18nForLocale(code).load(code, messages);
  // The global singleton still backs the `t` macro from @lingui/core/macro.
  i18n.load(code, messages);
  loadedLocales.add(code);
}

const catalogPromises = new Map<string, Promise<void>>();

export function loadCatalog(code: LanguageCode): Promise<void> {
  const cached = catalogPromises.get(code);
  if (cached) return cached;
  const promise = import(
    /* webpackChunkName: "locale-[request]", webpackInclude: /(en|es|fr|de|pt-br)[\\/]messages\.ts$/ */
    `../../../locales/${code}/messages`
  ).then(({ messages }) => {
    registerCatalog(code, messages);
  });
  catalogPromises.set(code, promise);
  return promise;
}

export async function activateLocale(code: LanguageCode): Promise<void> {
  if (!loadedLocales.has(code)) await loadCatalog(code);
  if (i18n.locale !== code) i18n.activate(code);
}

const LanguageContext = createContext<Language>(defaultLanguage as Language);
interface I18nProviderProps {
  language?: Language;
}

export const I18nProvider: React.FC<
  React.PropsWithChildren<I18nProviderProps>
> = ({ language = defaultLanguage, children }) => {
  // Only the global `t` macro users depend on this; <Trans> and useLingui()
  // read the per-locale instance from context and are immune to the race.
  if (i18n.locale !== language.code) {
    if (loadedLocales.has(language.code)) {
      i18n.activate(language.code);
    } else {
      // Corrective path only: the hydrate gate in gatsby-browser loads the
      // page's catalog before the first render, so this should never run.
      void activateLocale(language.code as LanguageCode);
    }
  }
  return (
    <LinguiProvider i18n={i18nForLocale(language.code)}>
      <LanguageContext.Provider value={language}>
        {children}
      </LanguageContext.Provider>
    </LinguiProvider>
  );
};

export function useLanguage(): Language {
  return React.useContext(LanguageContext);
}
